import { Check, ListFilter } from "lucide-react";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { SKUCommand } from "@/components/sku-command";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetCustomVerificationTypesQuery } from "@/data/queries";
import { TagVerificationTypeLabels, TagVerificationTypes } from "@/data/types";
import { getFilterCount } from "@/lib/pagination";
import { cn } from "@/lib/utils";

export const FilterDropdownMenu = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: customTypes } = useGetCustomVerificationTypesQuery();

  const handleSelectVerifyType = (verifyType: string) => {
    const tempParams = new URLSearchParams(searchParams);

    tempParams.delete("page");
    tempParams.set("verifyType", verifyType);

    setSearchParams(tempParams);
  };

  const handleSelectCustomVerifyType = (customVerificationTypeId: string) => {
    const tempParams = new URLSearchParams(searchParams);

    tempParams.delete("page");
    tempParams.set("customVerificationTypeId", customVerificationTypeId);

    setSearchParams(tempParams);
  };

  const filterCount = useMemo(
    () => getFilterCount(searchParams),
    [searchParams],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <ListFilter /> Filters{" "}
          {filterCount > 0 && <Badge>{filterCount}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[175px]">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>SKUs</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <SKUCommand />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Verification Type</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {TagVerificationTypes.map((v) => {
                return (
                  <DropdownMenuItem
                    key={v}
                    textValue={v}
                    onSelect={() => handleSelectVerifyType(v)}
                  >
                    {v === "custom" ? (
                      <span>All Custom Types</span>
                    ) : (
                      <span>{TagVerificationTypeLabels[v]}</span>
                    )}
                    <Check
                      className={cn(
                        "ml-auto",
                        searchParams.get("verifyType") === v
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Custom</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {customTypes?.map((v) => (
                    <DropdownMenuItem
                      key={v.id}
                      textValue={v.name}
                      onSelect={() => handleSelectCustomVerifyType(v.id)}
                    >
                      {v.name}
                      <Check
                        className={cn(
                          "ml-auto",
                          searchParams.get("customVerificationTypeId") === v.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
